import isNil from 'lodash-es/isNil';
import {
  UserMembership,
  CoursePurchaseMemberCoupon,
  AMOUNT_VALUE_TYPE,
  CouponDetails,
  Tier,
} from '@yi/core/src/coupons';
import { CourseSale } from '@yi/core/src/course-sale';
import { SaleValueType } from '@yi/core/src/gql/generated/typesClient';

import {
  getSubscriptionInfo,
  useAccessTier,
  useCurrentUserSubscription,
} from '~/src/common/subscription';
import { extractStringParamsFromUrlSearch } from '~/src/common/utilities';
import { useFeatureFlag } from '~/src/components/hooks/useFeatureFlag';
import { SalesCourse, SalesCourseBundle, UnreachableCaseError } from '~/src/types';

import { getBundleDiscount } from './getDiscount';
import { PriceInfo } from './PriceInfo';

type Params = {
  course?: SalesCourse;
  bundle?: SalesCourseBundle;
};

function convertSaleValueToPriceInfo(offer: CouponDetails | null) {
  if (offer === null) {
    return null;
  }
  const value = offer.value;
  const couponCode = offer.coupon_code ?? '';
  if (value === null) {
    return null;
  }
  const { amount, type } = value;

  if (type === AMOUNT_VALUE_TYPE) {
    return {
      value: {
        amountOff: amount,
      },
      couponCode,
    };
  }

  if (type === SaleValueType.PERCENTAGE) {
    return {
      value: {
        percentOff: amount,
      },
      couponCode,
    };
  }

  console.warn(new UnreachableCaseError(type));
  return null;
}

function convertCourseSaleToPriceInfo({
  offer,
  originalPrice,
  membershipTier,
}: {
  offer: CouponDetails | null;
  originalPrice: number;
  membershipTier: Tier | null;
}): PriceInfo {
  const discount = convertSaleValueToPriceInfo(offer);

  return {
    originalPrice,
    discount:
      discount !== null
        ? {
            ...discount,

            ...(membershipTier !== null && { membershipTier }),
          }
        : discount,
  };
}

function getCouponFromQueryString() {
  const { amount_off, percent_off, coupon } = extractStringParamsFromUrlSearch();
  if (isNil(coupon)) {
    return null;
  }

  const amountOff = amount_off ? parseInt(amount_off, 10) : 0;
  const percentOff = percent_off ? parseInt(percent_off, 10) : 0;
  const value = amountOff
    ? { type: AMOUNT_VALUE_TYPE, amount: amountOff }
    : { type: SaleValueType.PERCENTAGE, amount: percentOff };

  return {
    value,
    coupon_code: coupon,
  };
}

export function usePriceInfo({ bundle, course }: Params) {
  const { subscription } = useCurrentUserSubscription();
  const { tier: accessTier } = useAccessTier();
  const { userMembershipPlan } = getSubscriptionInfo(subscription, accessTier);

  const blackFridaySaleFeatureFlag = useFeatureFlag('black_friday');
  if (!isNil(bundle)) {
    const priceInfo = getBundleDiscount(bundle);
    return priceInfo;
  }

  if (isNil(course)) {
    return null;
  }

  const userMembership = new UserMembership(userMembershipPlan);
  const memberCoupon = new CoursePurchaseMemberCoupon(course, userMembership);
  const courseSale = new CourseSale(course);
  courseSale.addCoupon(memberCoupon.coupon());

  /**
   * if promo sale is started, you need to "start" sale, by running
   *  `courseSale.start();`
   * it's needed to be able to hide sale behind the feature flag
   * */
  if (blackFridaySaleFeatureFlag.enabled) {
    courseSale.start();
  } else {
    courseSale.stop();
  }

  const couponFromQueryString = getCouponFromQueryString();

  if (couponFromQueryString !== null) {
    courseSale.chooseCoupon(couponFromQueryString);
  }

  const bestOffer = courseSale.bestOffer();
  const membershipTier =
    bestOffer !== null && CoursePurchaseMemberCoupon.isMemberCoupon(bestOffer)
      ? userMembership.tier()
      : null;

  const priceInfo = convertCourseSaleToPriceInfo({
    offer: bestOffer,
    originalPrice: courseSale.fullPrice(),
    membershipTier,
  });

  return priceInfo;
}
